import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const ProtectedRoute = ({ children, requirePayment = false, allowLifetimeAccess = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, loading: authLoading, API } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      // Wait for auth to load
      if (authLoading) {
        return;
      }

      // Check token from context first, then fallback to localStorage
      const activeToken = token || localStorage.getItem('token');

      // Redirect to login if not authenticated
      if (!activeToken) {
        console.log('ProtectedRoute: No token, redirecting to login');
        navigate('/login');
        return;
      }

      // If payment is required, check payment status
      if (requirePayment) {
        try {
          console.log('ProtectedRoute: Checking payment status...');
          const response = await axios.get(
            `${API}/interpretation/status`,
            { headers: { Authorization: `Bearer ${activeToken}` } }
          );
          
          console.log('ProtectedRoute: Status response:', JSON.stringify(response.data));

          // For interpretation page: allow lifetime access OR active session
          if (allowLifetimeAccess) {
            const canAccess = response.data.can_view_interpretation || 
                            response.data.has_paid_access;
            
            console.log('ProtectedRoute (lifetime): canAccess =', canAccess);
            
            if (!canAccess) {
              console.log('ProtectedRoute: No lifetime access, redirecting to /introduction');
              navigate('/introduction');
              return;
            }
          } else {
            // For journaling/video pages: require paid access (includes unused access credit)
            // has_paid_access is true when:
            // - User has unused access credit (paid but timer not started)
            // - User has active session
            // - User is in free access list
            const canAccess = response.data.has_paid_access;
            
            console.log('ProtectedRoute (payment): canAccess =', canAccess, 'has_paid_access =', response.data.has_paid_access);
            
            if (!canAccess) {
              console.log('ProtectedRoute: No paid access, redirecting to /introduction');
              navigate('/introduction');
              return;
            }
          }

          setHasAccess(true);
        } catch (error) {
          console.error('ProtectedRoute: Error checking access:', error);
          navigate('/introduction');
          return;
        }
      } else {
        // No payment required, just authenticated
        setHasAccess(true);
      }

      setChecking(false);
    };

    checkAccess();
  }, [token, authLoading, requirePayment, allowLifetimeAccess, navigate, API]);

  // Show loading state
  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-[#E8E4DF] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-800 mx-auto mb-4"></div>
          <p 
            className="text-gray-600"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
          >
            Verifying access...
          </p>
        </div>
      </div>
    );
  }

  // Show the protected content if has access
  return hasAccess ? children : null;
};

export default ProtectedRoute;
